/**
 * The number on the Cortex "Bug-Reports" entry (#1135): reports with replies
 * the caller has not read — every report for admins, their own for everyone
 * else (the BFF decides the scope).
 *
 * One small request when Cortex opens (`GET /bug-reports/unread-count`, a
 * single COUNT in SQL — no report list), then only on a signal: a bug-report
 * notification arriving, or the caller's read marker moving in this browser
 * (opening a thread, posting a reply).
 *
 * `scope: 'mine'` counts only the caller's own tickets, admins included — the
 * standalone "Tickets" entry (#1176), whose `/tickets` page lists only those.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { useNotificationSignal } from '@/hooks/useNotificationSignal'
import { logError } from '@/lib/logger'
import { BUG_REPORT_READ_EVENT, bugReportService } from '@/services/bug-report-service'

export function useBugReportUnreadCount(scope: 'all' | 'mine' = 'all'): number {
  const [count, setCount] = useState(0)
  const latestRequest = useRef(0)

  const refresh = useCallback(() => {
    const request = ++latestRequest.current
    bugReportService
      .getUnreadCount(scope)
      .then((next) => {
        // A slower earlier answer must not overwrite a newer one.
        if (request === latestRequest.current) setCount(next)
      })
      .catch((error: unknown) => {
        // The badge is a hint; the tab itself still shows every report.
        logError(error instanceof Error ? error : new Error(String(error)), {
          context: 'useBugReportUnreadCount',
        })
      })
  }, [scope])

  useEffect(() => {
    refresh()
    window.addEventListener(BUG_REPORT_READ_EVENT, refresh)
    return () => window.removeEventListener(BUG_REPORT_READ_EVENT, refresh)
  }, [refresh])

  useNotificationSignal('bug_report_reply', refresh)
  useNotificationSignal('new_bug_report', refresh)
  useNotificationSignal('bug_report_status_changed', refresh)

  return count
}
